// =============================================================================
// Hierarchy Parser for QOps Actividades — Q-306
// Detects indentation in multi-line text to create parent → subtask groups.
// Runs as pre-processing BEFORE semanticParser.ts
// Pure TypeScript — no dependencies, no API calls, no React
// =============================================================================

export interface HierarchyGroup {
  /** Clean text of the parent line (prefixes stripped) */
  line: string
  /** Clean texts of child lines (prefixes stripped) */
  children: string[]
  /** 0-based index of the parent line in the original text */
  lineNumber: number
}

/**
 * Detect whether a line is indented (child) or top-level (parent/independent).
 * Returns the cleaned text with indentation and list prefixes removed.
 */
function parseLine(raw: string): { indented: boolean; text: string } {
  // Check for leading whitespace (tabs or 2+ spaces)
  const indentMatch = raw.match(/^(\t+|\s{2,})/)
  const hasIndent = !!indentMatch

  // Strip leading whitespace
  let text = raw.trimStart()

  // Strip list prefix (-, *, •) with optional trailing space
  const listMatch = text.match(/^[-*•]\s*/)
  if (listMatch) {
    text = text.slice(listMatch[0].length)
  }

  // A line is a child if it has indentation, regardless of list prefix.
  // A top-level line with only a list prefix (no indent) is independent.
  const indented = hasIndent

  return { indented, text: text.trim() }
}

/**
 * Parse multi-line text into hierarchy groups.
 *
 * Rules:
 * 1. Line without indentation and without list prefix → parent (or independent)
 * 2. Line with indentation (tabs or 2+ spaces) → subtask of previous parent
 * 3. Line without indentation but with list prefix (-/*) → independent activity
 * 4. Max 2 levels (parent → child). Deeper indentation flattens to child.
 * 5. Empty lines are ignored but don't break groups.
 *
 * @param text - Raw multi-line input
 * @returns Array of HierarchyGroup (groups with 0 children = independent activities)
 */
export function parseHierarchy(text: string): HierarchyGroup[] {
  const rawLines = text.split('\n')
  const groups: HierarchyGroup[] = []
  let currentGroup: HierarchyGroup | null = null

  for (let i = 0; i < rawLines.length; i++) {
    const raw = rawLines[i]

    // Skip empty lines (don't break current group)
    if (raw.trim().length === 0) continue

    const { indented, text: cleanText } = parseLine(raw)

    if (!cleanText) continue // line was only whitespace/prefix

    if (indented) {
      // Child line — attach to current parent
      if (currentGroup) {
        currentGroup.children.push(cleanText)
      } else {
        // Indented line without a parent — treat as independent
        groups.push({ line: cleanText, children: [], lineNumber: i })
      }
    } else {
      // Top-level line — start new group (previous group is finalized)
      currentGroup = { line: cleanText, children: [], lineNumber: i }
      groups.push(currentGroup)
    }
  }

  return groups
}

/**
 * Check if text contains hierarchy (has at least one indented line under a parent).
 * Useful for UI to decide whether to show hierarchy preview.
 */
export function hasHierarchy(text: string): boolean {
  return parseHierarchy(text).some((g) => g.children.length > 0)
}

/**
 * Flatten hierarchy groups back into a flat list of lines,
 * preserving the parent/child relationship as metadata.
 * Useful for building the preview table.
 */
export interface FlatHierarchyItem {
  text: string
  isChild: boolean
  groupIndex: number
  /** Index within the group (0 = parent, 1+ = children) */
  indexInGroup: number
  /** Total items in the group (1 = independent, 2+ = has children) */
  groupSize: number
}

export function flattenHierarchy(groups: HierarchyGroup[]): FlatHierarchyItem[] {
  const items: FlatHierarchyItem[] = []

  for (let gi = 0; gi < groups.length; gi++) {
    const group = groups[gi]
    const groupSize = 1 + group.children.length

    items.push({
      text: group.line,
      isChild: false,
      groupIndex: gi,
      indexInGroup: 0,
      groupSize,
    })

    for (let ci = 0; ci < group.children.length; ci++) {
      items.push({
        text: group.children[ci],
        isChild: true,
        groupIndex: gi,
        indexInGroup: ci + 1,
        groupSize,
      })
    }
  }

  return items
}
